import React, { useCallback, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Upload, FileSpreadsheet, CheckCircle2 } from 'lucide-react';
import { CSVDocumentation } from './CSVDocumentation';
import { ValidationErrors, ValidationError } from './ValidationErrors';
import { BuildingData } from './Step3Buildings';

export interface ClientData {
  meter_id: string;
  apartment_number: string;
  building_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

interface Step4CSVImportProps {
  clientsData: {
    csvFile: File | null;
    parsedData: ClientData[];
    validationErrors: ValidationError[];
    options: {
      sendWelcomeEmails: boolean;
      autoGenerateClientNumbers: boolean;
      createAccountsImmediately: boolean;
    };
  };
  buildings: BuildingData[];
  onChange: (field: string, value: ClientData[]) => void;
}

export const Step4CSVImport: React.FC<Step4CSVImportProps> = ({ clientsData, buildings, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateCSVData = useCallback((data: Record<string, string>[]): { valid: ClientData[]; errors: ValidationError[] } => {
    const errors: ValidationError[] = [];
    const validData: ClientData[] = [];
    const seenEmails = new Set<string>();
    const seenMeterIds = new Set<string>();

    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because CSV has header row and index starts at 0

      // Check required fields: meter_id, apartment_number, building_name
      if (!row.meter_id?.trim()) {
        errors.push({ row: rowNum, field: 'meter_id', message: 'Meter ID is required' });
      } else if (seenMeterIds.has(row.meter_id.trim().toUpperCase())) {
        errors.push({ row: rowNum, field: 'meter_id', message: 'Duplicate meter ID in CSV' });
      } else {
        seenMeterIds.add(row.meter_id.trim().toUpperCase());
      }

      if (!row.apartment_number?.trim()) {
        errors.push({ row: rowNum, field: 'apartment_number', message: 'Apartment number is required' });
      }

      if (!row.building_name?.trim()) {
        errors.push({ row: rowNum, field: 'building_name', message: 'Building name is required' });
      } else if (!buildings.find(b => b.name.toLowerCase() === row.building_name.trim().toLowerCase())) {
        errors.push({ row: rowNum, field: 'building_name', message: `Building "${row.building_name}" not found in Step 3` });
      }

      // Validate email format if provided (optional)
      if (row.email?.trim()) {
        if (!validateEmail(row.email.trim())) {
          errors.push({ row: rowNum, field: 'email', message: 'Invalid email format' });
        } else if (seenEmails.has(row.email.trim().toLowerCase())) {
          errors.push({ row: rowNum, field: 'email', message: 'Duplicate email in CSV' });
        } else {
          seenEmails.add(row.email.trim().toLowerCase());
        }
      }

      // If all required fields are valid, add to valid data
      if (row.meter_id?.trim() && row.apartment_number?.trim() && row.building_name?.trim() &&
          buildings.find(b => b.name.toLowerCase() === row.building_name.trim().toLowerCase()) &&
          !errors.some(e => e.row === rowNum)) {
        validData.push({
          meter_id: row.meter_id.trim(),
          apartment_number: row.apartment_number.trim(),
          building_name: row.building_name.trim(),
          first_name: row.first_name?.trim(),
          last_name: row.last_name?.trim(),
          email: row.email?.trim(),
          phone: row.phone?.trim(),
        });
      }
    });

    return { valid: validData, errors };
  }, [buildings]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File must be less than 10MB');
      return;
    }

    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    onChange('csvFile', file);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { valid, errors } = validateCSVData(results.data);
        onChange('parsedData', valid);
        onChange('validationErrors', errors);
      },
      error: (error) => {
        alert(`Error parsing CSV: ${error.message}`);
        onChange('csvFile', null);
      }
    });
  }, [onChange, validateCSVData]);

  // Re-validate CSV data when buildings change (e.g., user goes back to Step 3 and modifies buildings)
  useEffect(() => {
    if (clientsData.parsedData.length > 0) {
      // Re-validate existing parsed data against updated buildings
      const dataToRevalidate = clientsData.parsedData.map(client => ({
        meter_id: client.meter_id,
        apartment_number: client.apartment_number,
        building_name: client.building_name,
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
      }));

      const { valid, errors } = validateCSVData(dataToRevalidate);
      onChange('parsedData', valid);
      onChange('validationErrors', errors);
    }
  }, [buildings]); // Only re-run when buildings array changes

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileSpreadsheet className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Bulk Client Import</h2>
          <p className="text-muted-foreground">Upload a CSV file with client information</p>
        </div>
      </div>

      <CSVDocumentation />

      <div className="border rounded-lg p-6 bg-card space-y-4">
        <h3 className="font-semibold">Upload Client Data</h3>
        
        {clientsData.csvFile ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <p className="font-medium">{clientsData.csvFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {clientsData.parsedData.length} valid clients found
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  onChange('csvFile', null);
                  onChange('parsedData', []);
                  onChange('validationErrors', []);
                  // Reset file input so same file can be re-selected
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="text-destructive hover:underline text-sm"
              >
                Remove
              </button>
            </div>

            <ValidationErrors errors={clientsData.validationErrors} />

            {clientsData.parsedData.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted p-2">
                  <p className="text-sm font-medium">Preview (first 5 clients)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 text-left">Meter ID</th>
                        <th className="px-4 py-2 text-left">Building</th>
                        <th className="px-4 py-2 text-left">Apt #</th>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientsData.parsedData.slice(0, 5).map((client, index) => (
                        <tr key={index} className="border-t">
                          <td className="px-4 py-2 font-mono text-xs">{client.meter_id}</td>
                          <td className="px-4 py-2">{client.building_name}</td>
                          <td className="px-4 py-2">{client.apartment_number}</td>
                          <td className="px-4 py-2">
                            {client.first_name || client.last_name 
                              ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
                              : '-'}
                          </td>
                          <td className="px-4 py-2">{client.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {clientsData.parsedData.length > 5 && (
                  <div className="bg-muted/50 p-2 text-center text-sm text-muted-foreground">
                    ... and {clientsData.parsedData.length - 5} more clients
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <label
            htmlFor="csvUpload"
            className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-12 cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <Upload className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">Click to upload CSV file</p>
            <p className="text-sm text-muted-foreground">Or drag and drop your file here</p>
            <p className="text-xs text-muted-foreground mt-2">Maximum file size: 10MB</p>
          </label>
        )}
        <input
          ref={fileInputRef}
          id="csvUpload"
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      <div className="border rounded-lg p-6 bg-card space-y-4">
        <h3 className="font-semibold">Import Options</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="sendEmails">Send Welcome Emails</Label>
              <p className="text-sm text-muted-foreground">Send email to each client with login credentials</p>
            </div>
            <Switch
              id="sendEmails"
              checked={clientsData.options.sendWelcomeEmails}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, sendWelcomeEmails: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="autoGenerate">Auto-generate Client Numbers</Label>
              <p className="text-sm text-muted-foreground">Automatically create unique client IDs</p>
            </div>
            <Switch
              id="autoGenerate"
              checked={clientsData.options.autoGenerateClientNumbers}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, autoGenerateClientNumbers: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="createAccounts">Create Accounts Immediately</Label>
              <p className="text-sm text-muted-foreground">Create user accounts right away (vs draft mode)</p>
            </div>
            <Switch
              id="createAccounts"
              checked={clientsData.options.createAccountsImmediately}
              onCheckedChange={(checked) =>
                onChange('options', { ...clientsData.options, createAccountsImmediately: checked })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
